import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import {
  ChatPropertyType,
  ConsultingProgress,
  MatchingType,
  MediationContractCategory,
  Priority,
  RefererEvent,
  ReportRecordReminderEvidenceType,
  ReportRecordReportedBy,
  SellingStatus,
} from '@constants_iqfu';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export type SellerOperationalNoteResponseType = {
  comment: string;
  consulting_progress: keyof typeof ConsultingProgress | null;
  next_contact_date: string | null;
  last_contact_date: string | null;
  past_contact_dates: string[] | null;
  only_interested: boolean;
  priority: keyof typeof Priority;
  property_owner_memo: string | null;
  referer_event: keyof typeof RefererEvent | null;
  aggregation_excluded: boolean;
  labels: {
    key: string;
    name: string;
  }[];
  property_labels: {
    chat_property_id: number;
    labels: {
      key: string;
      name: string;
      sort_key: number;
    }[];
  }[];
};

export type UpdateSellerOperationNotePayload = {
  comment?: string;
  next_contact_date?: string | null;
  last_contact_date?: string | null;
  consulting_progress?: keyof typeof ConsultingProgress | null;
  only_interested?: boolean;
  priority?: keyof typeof Priority | null;
  property_owner_memo?: string | null;
  aggregation_excluded?: boolean;
};

export type UpdateCompanyOperationalNotePayload = {
  next_contact_date?: string;
  last_contact_date?: string;
  next_contact_admin_user?: string;
  last_contact_admin_user?: string;
};

export type SellingStatusChangedByType = 'manual' | 'system_trigger' | 'llm';

export type ChatOperationalNoteResponseType = {
  agreement_report_id: number | null; // 売買契約報告ID
  comment: string; // メモ
  chat_room_id: number; // 売却相談チャットのID
  inquiry_seller_room_id: number; // 売主 × 運営チャットのID
  inquiry_company_room_id: number; // 不動産会社 × 運営チャットのID
  matching_type: keyof typeof MatchingType; // 送客経路
  tel_allowed: boolean; // 電話連絡可否
  tel_allowed_reason: string | null; // 電話連絡可否の理由
  seller_user_sending_date: string; // 送客日
  desk_appraisal_date: string; // 机上査定日
  on_site_appraisal_date: string; // 訪問査定日
  seller_mediation_report_date: string; // 媒介報告日（売主）
  company_mediation_report_date: string; // 媒介報告日（不動産会社）
  selling_by_iqra_company_date: string; // 加盟店売却中判明日
  selling_by_others_date: string; // 他社売却中判明日
  seller_contract_report_date: string; // 成約予定報告日（売主）
  company_contract_report_date: string; // 成約予定報告日（不動産会社）
  sold_by_other_company_date: string; // 他社売却完了判明日
  mediation_contract_date: string; // 媒介契約締結日
  mediation_contract_amount: number | null; // 媒介価格
  mediation_contract_category: keyof typeof MediationContractCategory | null; // 媒介種別
  mediation_agreement_report_id: number | null; // 媒介契約報告ID
  selling_contract_date: string; // 売買契約締結日
  selling_contract_amount: number | null; // 成約価格
  settlement_date: string; // 決済日
  scheduled_settlement_date: string; // 決済予定日
  iqra_commission: number | null; // イクラ報酬(税抜)
  billing_date: string; // 請求月
  on_site_appraisal_requested: boolean | null; // 訪問査定済か否かのフラグ
  on_site_appraisal_memo: string | null; // 訪問査定メモ
  mediation_contract_uploaded: boolean | null; // 媒介契約書アップロード
  selling_contract_uploaded: boolean | null; // 売買契約書アップロード
  seller_mediation_agreement_report_uploaded: boolean | null; // 売主版媒介契約書アップロード
  seller_sale_agreement_report_uploaded: boolean | null; // 売主版売買契約書アップロード
  receipt_uploaded: boolean | null; // 領収書アップロード
  selling_status: keyof typeof SellingStatus | null; // 売却相談のステータス
  last_selling_status_changed_by_type: SellingStatusChangedByType | null; // 最後の売却ステータス更新の種別
  is_listed_without_mediation: boolean; // 媒介を結ばず売り出し中フラグ
  priority: keyof typeof Priority; // 優先度
  // 売買報告記録
  agreement_report_reported_by: keyof typeof ReportRecordReportedBy | null;
  agreement_report_reminder_evidence_type: keyof typeof ReportRecordReminderEvidenceType | null;
  agreement_report_reported_date: string | null;
  // 媒介報告記録
  mediation_agreement_report_reported_by: keyof typeof ReportRecordReportedBy | null;
  mediation_agreement_report_reminder_evidence_type: keyof typeof ReportRecordReminderEvidenceType | null;
  mediation_agreement_report_reported_date: string | null;
};

export type UpdateChatOperationNotePayload = {
  comment?: string; // メモ
  matching_type?: keyof typeof MatchingType; // 送客経路
  tel_allowed?: boolean; // 電話連絡可否
  tel_allowed_reason?: string | null; // 電話連絡可否の理由
  seller_user_sending_date?: string; // 送客日
  desk_appraisal_date?: string; // 机上査定日
  on_site_appraisal_date?: string; // 訪問査定日
  seller_mediation_report_date?: string; // 媒介報告日（売主）
  company_mediation_report_date?: string; // 媒介報告日（不動産会社）
  selling_by_iqra_company_date?: string; // 加盟店売却中判明日
  selling_by_others_date?: string; // 他社売却中判明日
  seller_contract_report_date?: string; // 成約予定報告日（売主）
  company_contract_report_date?: string; // 成約予定報告日（不動産会社）
  sold_by_other_company_date?: string; // 他社売却完了判明日
  mediation_contract_date?: string; // 媒介契約締結日
  mediation_contract_amount?: number | null; // 媒介価格
  mediation_contract_category?: keyof typeof MediationContractCategory | null; // 媒介種別
  selling_contract_date?: string; // 売買契約締結日
  selling_contract_amount?: number | null; // 成約価格
  settlement_date?: string; // 決済日
  scheduled_settlement_date?: string; // 決済予定日
  iqra_commission?: number | null; // イクラ報酬(税抜)
  billing_date?: string; // 請求月
  on_site_appraisal_requested?: boolean | null; // 訪問査定済か否かのフラグ
  on_site_appraisal_memo?: string | null; // 訪問査定メモ
  mediation_contract_uploaded?: boolean | null; // 媒介契約書アップロード
  selling_contract_uploaded?: boolean | null; // 売買契約書アップロード
  receipt_uploaded?: boolean | null; // 領収書アップロード
  selling_status?: keyof typeof SellingStatus | null; // 売却相談のステータス
  is_listed_without_mediation?: boolean; // 媒介を結ばず売り出し中フラグ
  priority?: keyof typeof Priority | null; // 優先度
  // 売買報告記録
  agreement_report_reported_by?: keyof typeof ReportRecordReportedBy | null;
  agreement_report_reminder_evidence_type?: keyof typeof ReportRecordReminderEvidenceType | null;
  agreement_report_reported_date?: string | null;
  // 媒介報告記録
  mediation_agreement_report_reported_by?: keyof typeof ReportRecordReportedBy | null;
  mediation_agreement_report_reminder_evidence_type?: keyof typeof ReportRecordReminderEvidenceType | null;
  mediation_agreement_report_reported_date?: string | null;
};

export type ChatEvaluationResponseType = {
  response_speed_score: number | null;
  politeness_diligence_score: number | null;
  customer_support_score: number | null;
  problem_solving_score: number | null;
  customer_satisfaction_score: number | null;
  updated_at: string;
};

export type UpdateChatEvaluationPayload = {
  response_speed_score: number | null;
  politeness_diligence_score: number | null;
  customer_support_score: number | null;
  problem_solving_score: number | null;
  customer_satisfaction_score: number | null;
};

export const LatestDataKey = {
  desk_appraisal_date: '机上査定日',
  on_site_appraisal_date: '訪問査定日',
  seller_mediation_report_date: '媒介報告日（売主）',
  company_mediation_report_date: '媒介報告日（不動産会社）',
  selling_by_iqra_company_date: '加盟店売却中判明日',
  selling_by_others_date: '他社売却中判明日',
  seller_contract_report_date: '成約予定報告日（売主）',
  company_contract_report_date: '成約予定報告日（不動産会社）',
  sold_by_other_company_date: '他社売却完了判明',
};

type Latest = {
  chat_room_id: number;
  company_id: number;
  company_name: string;
  company_display_name: string;
  date: Date;
  date_key: keyof typeof LatestDataKey;
  company_type: 'general_agency' | 'purchase_agency';
};

export type ChatProgress = {
  id: number;
  address: string;
  property_type: keyof typeof ChatPropertyType;
  latest_selling_status: keyof typeof SellingStatus | null;
  latest: Latest[];
};

export type ChatProgressResponseType = {
  chat_properties: ChatProgress[];
};

export type GetCompanyOperationalNote = {
  comment: string;
  admin_sales_representative: string;
  next_contact_date: string;
  next_contact_admin_user: string;
  last_contact_date: string;
  last_contact_admin_user: string;
  past_contact_dates: { date: string; user: string }[];
};

export const useOperationalNote = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getChatEvaluation = async ({ chatRoomId }: { chatRoomId: string }): Promise<ChatEvaluationResponseType> => {
    const url = `${backendBasePath}/admin/chat_rooms/${chatRoomId}/chat_evaluation`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateChatEvaluation = async (params: {
    chatRoomId: string;
    payload: UpdateChatEvaluationPayload;
  }): Promise<{ success: boolean }> => {
    const url = `${backendBasePath}/admin/chat_rooms/${params.chatRoomId}/chat_evaluation`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params.payload),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getOfflineSellerLeadEvaluation = async ({
    offlineSellerLeadId,
  }: {
    offlineSellerLeadId: string;
  }): Promise<ChatEvaluationResponseType> => {
    const url = `${backendBasePath}/admin/offline_seller_leads/${offlineSellerLeadId}/evaluation`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateOfflineSellerLeadEvaluation = async (params: {
    offlineSellerLeadId: string;
    payload: UpdateChatEvaluationPayload;
  }): Promise<{ success: boolean }> => {
    const url = `${backendBasePath}/admin/offline_seller_leads/${params.offlineSellerLeadId}/evaluation`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params.payload),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getChatCompanyInquiryEvaluation = async ({
    chatRoomId,
  }: {
    chatRoomId: string;
  }): Promise<ChatEvaluationResponseType> => {
    const url = `${backendBasePath}/admin/chat_company_inquiries/${chatRoomId}/chat_evaluation`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateChatCompanyInquiryEvaluation = async (params: {
    chatRoomId: string;
    payload: UpdateChatEvaluationPayload;
  }): Promise<{ success: boolean }> => {
    const url = `${backendBasePath}/admin/chat_company_inquiries/${params.chatRoomId}/chat_evaluation`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params.payload),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getChatOperationalNote = async ({
    chatRoomId,
  }: {
    chatRoomId: string;
  }): Promise<ChatOperationalNoteResponseType> => {
    const url = `${backendBasePath}/admin/chat_rooms/${chatRoomId}/operational_note`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateChatOperationalNote = async (params: {
    chatRoomId: string;
    payload: UpdateChatOperationNotePayload;
  }): Promise<{ success: boolean }> => {
    const url = `${backendBasePath}/admin/chat_rooms/${params.chatRoomId}/operational_note`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params.payload),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSellerUserOperationalNote = async ({
    SellerUserId,
  }: {
    SellerUserId: string;
  }): Promise<SellerOperationalNoteResponseType> => {
    const url = `${backendBasePath}/admin/seller_users/${SellerUserId}/operational_note`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateSellerOperationalNote = async (params: {
    SellerUserId: string;
    payload: UpdateSellerOperationNotePayload;
  }): Promise<{ success: boolean }> => {
    const url = `${backendBasePath}/admin/seller_users/${params.SellerUserId}/operational_note`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params.payload),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSellerChatProgress = async ({
    SellerUserId,
  }: {
    SellerUserId: string;
  }): Promise<ChatProgressResponseType> => {
    const url = `${backendBasePath}/admin/seller_users/${SellerUserId}/operational_note/selling_chat_progress`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSellerMemo = async ({ sellerUserId }: { sellerUserId: string }) => {
    const url = `${backendBasePath}/admin/seller_users/${sellerUserId}/operational_note`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateSellerMemo = async ({ sellerUserId, comment }: { sellerUserId: string; comment: string }) => {
    const url = `${backendBasePath}/admin/seller_users/${sellerUserId}/operational_note`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ comment }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };
  const getCompanyOperationalNote = async ({
    companyId,
  }: {
    companyId: string;
  }): Promise<GetCompanyOperationalNote> => {
    const url = `${backendBasePath}/admin/companies/${companyId}/operational_note`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateCompanyOperationalNote = async ({
    companyId,
    comment,
    adminSalesRepresentative,
    salesComment,
    contactDates,
  }: {
    companyId: string;
    comment?: string;
    adminSalesRepresentative?: string;
    salesComment?: string;
    contactDates?: UpdateCompanyOperationalNotePayload;
  }) => {
    const url = `${backendBasePath}/admin/companies/${companyId}/operational_note`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        comment,
        admin_sales_representative: adminSalesRepresentative,
        sales_comment: salesComment,
        next_contact_date: contactDates?.next_contact_date,
        next_contact_admin_user: contactDates?.next_contact_admin_user,
        last_contact_date: contactDates?.last_contact_date,
        last_contact_admin_user: contactDates?.last_contact_admin_user,
      }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  return {
    getChatOperationalNote,
    updateChatOperationalNote,
    getSellerUserOperationalNote,
    updateSellerOperationalNote,
    getChatEvaluation,
    updateChatEvaluation,
    getChatCompanyInquiryEvaluation,
    updateChatCompanyInquiryEvaluation,
    getOfflineSellerLeadEvaluation,
    updateOfflineSellerLeadEvaluation,
    getSellerChatProgress,
    getSellerMemo,
    updateSellerMemo,
    getCompanyOperationalNote,
    updateCompanyOperationalNote,
  };
};
